import { AccountBalance, Dashboard as DashboardIcon, Group, Search, NotificationsOutlined, KeyboardArrowDown, Assessment, Analytics, Receipt, DeleteOutline } from '@mui/icons-material';
import { Avatar, Box, IconButton, Typography, InputBase, Badge, Menu, MenuItem } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import BottomNavBar from './bottomNavBar';
import AdBanner from './AdBanner';
import RenewalBanner from './RenewalBanner';
import { firebaseGoogleSignOut } from '../firebase/authHandler';
import { getPreferences, getAccountsList } from '../firebase/dataHandler';
import AppBreadcrumbs from './AppBreadcrumbs';
import { useTheme } from '../context/ThemeContext';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { APP_VERSION } from '../config/version';

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export default function SidebarLayout({ user }) {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { t } = useTranslation();

  // Subscription expiry state
  const [premiumExpiresAt, setPremiumExpiresAt] = useState(null);
  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => {
    getPreferences().then(prefs => {
      setIsPremium(prefs.isPremium === true);
      setPremiumExpiresAt(prefs.premiumExpiresAt || null);
    }).catch(console.error);
  }, []);

  // Derive banner variant
  const remaining = premiumExpiresAt ? premiumExpiresAt - Date.now() : null;
  const daysLeft = remaining !== null ? Math.max(0, Math.ceil(remaining / (24 * 60 * 60 * 1000))) : null;
  const showExpiredBanner  = !isPremium && premiumExpiresAt && remaining < 0;
  const showExpiringBanner = isPremium  && premiumExpiresAt && remaining > 0 && remaining < SEVEN_DAYS_MS;

  const renewalBanner = showExpiredBanner
    ? <RenewalBanner variant="expired"  expiresAt={premiumExpiresAt} />
    : showExpiringBanner
      ? <RenewalBanner variant="expiring" expiresAt={premiumExpiresAt} daysLeft={daysLeft} />
      : null;

  // Resize listener
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // If mobile, just render the child route and the bottom navbar  // --- MOBILE APP LAYOUT ---
  if (!isDesktop) {
    const showBottomNav = (location.pathname === '/' || location.pathname === '/dashboard' || location.pathname === '/accounts' || location.pathname === '/customers' || location.pathname === '/profile' || location.pathname === '/reports' || location.pathname === '/trash');
    
    return (
      <Box sx={{ position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--bg-base)' }}>
        {/* Renewal banner — sticky at top on mobile */}
        {renewalBanner && (
          <Box sx={{ flexShrink: 0, zIndex: 100 }}>{renewalBanner}</Box>
        )}
        <Box sx={{ flex: 1, overflowY: 'auto', pb: showBottomNav ? '72px' : 0, display: 'flex', flexDirection: 'column' }}>
          <AppBreadcrumbs />
          <Outlet />
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: 10, textAlign: 'center', color: 'var(--text-muted)', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
              Finaculator Console {APP_VERSION} • Biatom Tech Solutions
            </Typography>
          </Box>
        </Box>
        
        {showBottomNav && (
          <BottomNavBar user={user} isPremium={isPremium} />
        )}
      </Box>
    );
  }

  // --- DESKTOP PRO LAYOUT ---
  return (
    <Box className="desktop-layout" sx={{ position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, display: 'flex', overflow: 'hidden', background: 'var(--bg-base)' }}>
      
      {/* 1. Professional Sidebar (Left) */}
      <Box sx={{ 
        width: '260px', 
        display: 'flex', flexDirection: 'column', 
        borderRight: '1px solid var(--border-subtle)', background: 'var(--bg-surface)',
        zIndex: 10, flexShrink: 0
      }}>
        {/* Logo / Brand Area */}
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid var(--border-subtle)' }}>
          <img src={theme === 'light' ? require('../assets/site/long- squar-logo-fin-dark.svg').default : require('../assets/site/long- squar-logo-fin-light.svg').default} alt="Finaculator" style={{ width: '100%', maxWidth: '180px', height: 'auto' }} />
        </Box>

        {/* Navigation Links */}
        <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1, mt: 2, px: 2 }}>
            Main Menu
          </Typography>
          <NavButton icon={<DashboardIcon />} label={t('nav.dashboard')} path="/dashboard" currentPath={location.pathname} navigate={navigate} isPremiumFeature={true} isPremiumUser={isPremium} />
          <NavButton icon={<AccountBalance />} label={t('nav.accounts')} path="/accounts" currentPath={location.pathname} navigate={navigate} />
          <NavButton icon={<Group />} label={t('nav.customers')} path="/customers" currentPath={location.pathname} navigate={navigate} />
          <NavButton icon={<Receipt />} label={t('nav.invoices')} path="/invoices" currentPath={location.pathname} navigate={navigate} />
          <NavButton icon={<Analytics />} label={t('nav.reports')} path="/reports" currentPath={location.pathname} navigate={navigate} isPremiumFeature={true} isPremiumUser={isPremium} />
          <NavButton icon={<DeleteOutline />} label="Trash" path="/trash" currentPath={location.pathname} navigate={navigate} />
        </Box>

        {/* User Profile Mini (Bottom of Sidebar) */}
        <Box sx={{ p: 2, borderTop: '1px solid var(--border-subtle)' }}>
          <Box 
            onClick={() => navigate('/profile')}
            sx={{ 
              display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: 2, cursor: 'pointer',
              '&:hover': { background: 'var(--bg-card-hover)' }
            }}
          >
            <Avatar src={user?.photoURL} sx={{ width: 36, height: 36 }} />
            <Box sx={{ overflow: 'hidden' }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                {user?.displayName || 'User'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                Financier
              </Typography>
            </Box>
          </Box>

          <Typography sx={{ fontSize: 10, textAlign: 'center', color: 'var(--text-muted)', mt: 3, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
            Finaculator {APP_VERSION} • Biatom Tech Solutions
          </Typography>
        </Box>
      </Box>

      {/* 2. Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        
        {/* Top App Bar */}
        <TopAppBar user={user} navigate={navigate} isPremium={isPremium} />

        {/* Renewal notification banner */}
        {renewalBanner}

        {/* Scrollable Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', background: 'var(--bg-base)', position: 'relative', display: 'flex', flexDirection: 'column' }}>
          <AppBreadcrumbs />
          <Box sx={{ flex: 1, width: '100%', p: { xs: 2, md: 4 }, pb: 10 }}>
            {/* Top Ad Banner Space (Optional) */}
            <Box sx={{ mb: 4, display: 'none' }}>
              <AdBanner type="horizontal" />
            </Box>
            
            <Outlet />
          </Box>
        </Box>
      </Box>

      {/* 3. Right Ad Banner / Widgets Panel */}
      <Box sx={{ 
        width: '300px', 
        borderLeft: '1px solid var(--border-subtle)', 
        background: 'var(--bg-surface)',
        display: { xs: 'none', xl: 'block' },
        overflow: 'hidden',
        p: 2
      }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
          Sponsored
        </Typography>
        <AdBanner type="vertical" />
      </Box>
    </Box>
  );
}

// Sidebar Nav Button Component
function NavButton({ icon, label, path, currentPath, navigate, isPremiumFeature, isPremiumUser }) {
  const isActive = currentPath === path || currentPath === (path === '/dashboard' ? '/' : 'some-impossible-path');
  return (
    <Box 
      onClick={() => navigate(path)}
      sx={{ 
        display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.2, borderRadius: 2, cursor: 'pointer',
        color: isActive ? 'var(--accent-green)' : 'var(--text-secondary)',
        background: isActive ? 'var(--bg-card-hover)' : 'transparent',
        transition: 'all 0.2s ease',
        '&:hover': { background: 'var(--bg-card-hover)', color: isActive ? 'var(--accent-green)' : 'var(--text-primary)' }
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: 22 } })}
      <Typography sx={{ fontWeight: isActive ? 600 : 500, fontSize: 15, fontFamily: 'Inter' }}>
        {label}
      </Typography>
      {isPremiumFeature && !isPremiumUser && (
        <Box sx={{ ml: 'auto', background: 'rgba(255, 215, 0, 0.15)', px: 1, py: 0.2, borderRadius: 1, border: '1px solid rgba(255, 215, 0, 0.4)' }}>
           <Typography sx={{ fontSize: 9, fontWeight: 800, color: '#FFD700' }}>PRO</Typography>
        </Box>
      )}
    </Box>
  );
}

// Top App Bar Component
function TopAppBar({ user, navigate, isPremium }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [globalSearch, setGlobalSearch] = useState('');
  const [overdueAccounts, setOverdueAccounts] = useState([]);

  useEffect(() => {
    if (user) {
      getAccountsList().then(list => {
        if (!list) return;
        const overdues = Object.values(list).filter(a => {
           if (a.isActive === false) return false;
           let paidDayCount = a.entries ? Object.values(a.entries).reduce((acc, sum) => acc + Number(sum.dayCount), 0) : 0;
           let elapsedDays = Math.round((Date.now() - new Date(a.date).getTime()) / 86400000);
           
           const d = new Date(a.date);
           const today = new Date();
           let elapsedPeriods = a.installmentType === 'daily' ? elapsedDays 
             : a.installmentType === 'weekly' ? Math.round(elapsedDays / 7) 
             : a.installmentType === 'monthly' ? ((today.getFullYear() - d.getFullYear()) * 12 + today.getMonth() - d.getMonth()) : 0;
             
           return (elapsedPeriods > a.installmentPeriod) && ((a.installmentPeriod - paidDayCount) > 0);
        });
        setOverdueAccounts(overdues);
      }).catch(console.error);
    }
  }, [user]);

  const handleSearch = (e) => {
    if (e.key === 'Enter' && globalSearch.trim()) {
      navigate('/accounts?q=' + encodeURIComponent(globalSearch.trim()));
      setGlobalSearch('');
    }
  };
  
  return (
    <Box sx={{ 
      height: '72px', flexShrink: 0, 
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
      px: 4, background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-subtle)' 
    }}>
      {/* Search */}
      <Box sx={{ 
        display: 'flex', alignItems: 'center', gap: 1, 
        background: 'var(--bg-base)', px: 2, py: 1, borderRadius: 2,
        border: '1px solid var(--border-subtle)', width: '320px'
      }}>
        <Search sx={{ color: 'var(--text-muted)', fontSize: 20 }} />
        <InputBase 
          placeholder="Search accounts or customers... (Press Enter)" 
          value={globalSearch}
          onChange={(e) => setGlobalSearch(e.target.value)}
          onKeyDown={handleSearch}
          sx={{ flex: 1, fontSize: 14, fontFamily: 'Inter', color: 'var(--text-primary)' }} 
        />
      </Box>

      {/* Right Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <LanguageSwitcher isPremium={isPremium} />
        
        <IconButton sx={{ color: 'var(--text-secondary)' }} onClick={(e) => setNotifAnchorEl(e.currentTarget)}>
          <Badge badgeContent={overdueAccounts.length} color="error" sx={{ '& .MuiBadge-badge': { background: 'var(--accent-red)' } }}>
            <NotificationsOutlined />
          </Badge>
        </IconButton>

        {/* Notifications Dropdown */}
        <Menu
          anchorEl={notifAnchorEl}
          open={Boolean(notifAnchorEl)}
          onClose={() => setNotifAnchorEl(null)}
          PaperProps={{ sx: { mt: 1, borderRadius: 3, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', minWidth: '240px', boxShadow: 'var(--shadow-md)' } }}
        >
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid var(--border-subtle)' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Notifications</Typography>
          </Box>
          {overdueAccounts.length > 0 ? (
            <MenuItem onClick={() => { setNotifAnchorEl(null); navigate('/accounts'); }} sx={{ fontSize: 14, fontFamily: 'Inter', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography sx={{ color: 'var(--accent-red)', fontWeight: 600, fontSize: 13 }}>{overdueAccounts.length} Overdue Accounts</Typography>
              <Typography sx={{ color: 'var(--text-secondary)', fontSize: 12 }}>Click here to view them.</Typography>
            </MenuItem>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography sx={{ color: 'var(--text-muted)', fontSize: 13 }}>No new notifications.</Typography>
            </Box>
          )}
        </Menu>
        
        <Box sx={{ width: '1px', height: '24px', background: 'var(--border-subtle)', mx: 1 }} />

        {/* Profile Dropdown */}
        <Box 
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', p: 0.5, borderRadius: 1.5, '&:hover': { background: 'var(--bg-base)' } }}
        >
          <Avatar src={user?.photoURL} sx={{ width: 32, height: 32 }} />
          <KeyboardArrowDown sx={{ color: 'var(--text-muted)', fontSize: 18 }} />
        </Box>
        
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          PaperProps={{ sx: { mt: 1, borderRadius: 3, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', minWidth: '160px', boxShadow: 'var(--shadow-md)' } }}
        >
          <MenuItem onClick={() => { setAnchorEl(null); navigate('/profile'); }} sx={{ fontSize: 14, fontFamily: 'Inter' }}>Profile & Settings</MenuItem>
          <MenuItem onClick={() => { setAnchorEl(null); navigate('/subscription'); }} sx={{ fontSize: 14, fontFamily: 'Inter' }}>Subscription</MenuItem>
          <Box sx={{ my: 1, height: '1px', background: 'var(--border-subtle)' }} />
          <MenuItem onClick={() => { setAnchorEl(null); firebaseGoogleSignOut(); }} sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--accent-red)' }}>Sign out</MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}
